import { buildAnnotatedImageAssignments } from '../../../utils/annotatedImages';
import React, { useState } from 'react';
import { ShoppingBag, Loader2, History, ChevronDown, ChevronUp, PencilLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Message, StructuredAnswer } from './types';
import UserMessage from './UserMessage';
import BotMessage, { ValidationPartsPayload, SummaryItem, normalizeClarificationQuestions } from './BotMessage';
import { PreviousClarificationStep } from './StructuredQuestionForm';
import StreamingIndicator from './StreamingIndicator';
import CatalogStatusStrip from './CatalogStatusStrip';
import { getVersionedPublicUrl } from '../../../utils/versionUtils';
import { ConversationMessage } from '../../../services/OrganizationService';
import { isPersistedPartEditInstruction, parsePersistedPartEdits, partEditKind } from '../../../utils/partEdits';
import { rendersResults as rendersResultsFor } from '../../../utils/latestResults';

/** Convert a raw guest-history entry (from session state) into a full Message object
 *  so it can be rendered by the same UserMessage / BotMessage components. */
function guestMsgToMessage(msg: any): Message | null {
  const sender = (msg.sender ?? msg.role) as string;
  const isUser = sender === 'user';

  // Skip internal NEXT auto-trigger messages and SYSTEM_MESSAGE messages. Part
  // edits remain in the list so they can render as a compact history indicator.
  if (isUser && String(msg.text ?? msg.content ?? '').trim().toUpperCase() === 'NEXT') {
    return null;
  }
  if (isUser
    && String(msg.text ?? msg.content ?? '').includes('SYSTEM_MESSAGE:')
    && !isPersistedPartEditInstruction(String(msg.text ?? msg.content ?? ''))) {
    return null;
  }

  // Resolve display text — bot messages arrive pre-cleaned from the backend,
  // but defensively handle raw JSON for sessions converted before the fix.
  let text: string = msg.text ?? msg.content ?? '';
  if (!isUser && text) {
    try {
      const parsed = JSON.parse(text);
      text = parsed.text_message ?? text;
    } catch { /* already plain text */ }
    // Strip residual HTML tags
    text = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  if (!text && !msg.uploadedFiles?.length) return null;

  // Parse timestamp: backend stores ISO strings or Unix floats. Intake notes
  // carry it as `ts`; without reading it every note showed the page-load time.
  let timestamp: Date;
  if (msg.timestamp ?? msg.ts) {
    const raw = msg.timestamp ?? msg.ts;
    if (typeof raw === 'number') {
      // Unix seconds → ms
      timestamp = new Date(raw < 1e12 ? raw * 1000 : raw);
    } else {
      timestamp = new Date(raw);
    }
    if (isNaN(timestamp.getTime())) timestamp = new Date();
  } else {
    timestamp = new Date();
  }

  return {
    id: msg.id ?? `guest-${sender}-${timestamp.getTime()}`,
    text,
    sender: isUser ? 'user' : 'bot',
    timestamp,
    vin: null,
    datasetId: null,
    isStreaming: false,
    isError: false,
    // Carry over rich fields if present (parts results, etc.)
    parts_results: msg.parts_result ?? undefined,
    annotated_images: msg.annotated_images ?? null,
    repair_instructions: msg.repair_instructions ?? null,
    search_summarization: msg.search_summarization ?? null,
    not_found_parts: msg.not_found_parts ?? null,
    requested_parts: msg.requested_parts ?? null,
    lines: msg.lines ?? null,
    turn_lines: msg.turn_lines ?? null,
    uploadedFiles: msg.uploadedFiles ?? undefined,
  };
}

/** Returns true when a bot message has no displayable content and should be hidden. */
/** A bot message's clarification envelope, or null. */
function clarificationOf(m: Message | undefined): { questions: any[]; answers: any; requestLines: Record<string, { original: string; label: string }> } | null {
  if (!m || m.sender !== 'bot') return null;
  const t = (m.text || '').trim();
  if (!t.startsWith('{') || !t.includes('"clarification_questions"')) return null;
  try {
    const o = JSON.parse(t);
    if (!(Array.isArray(o?.clarification_questions) && o.clarification_questions.length)) return null;
    // The request line each question is about — the customer's own words —
    // so an earlier step folded into the card still says what was asked for.
    const requestLines: Record<string, { original: string; label: string }> = {};
    for (const l of ((m as any).lines ?? o?.lines ?? []) as any[]) {
      if (l?.question_id) requestLines[l.question_id] = { original: String(l.original ?? ''), label: String(l.label || l.canonical || l.original || '') };
    }
    return { questions: o.clarification_questions, answers: o.clarification_answers ?? null, requestLines };
  } catch {
    return null;
  }
}

/** `{current, total}` when this question is one step of a planned run.
 *  Written by the backend planner (`clarification_planner.progress`) and shown
 *  as "Question 2 of 7" — so it also tells us the two bubbles are one card. */
function planStepOf(env: { questions: any[] } | null): { current: number; total: number } | null {
  const step = (env?.questions || [])[0]?.plan_step;
  const current = Number(step?.current);
  const total = Number(step?.total);
  return Number.isFinite(current) && Number.isFinite(total) ? { current, total } : null;
}

/** How a run of clarification questions renders as ONE card: which messages
 *  are folded into the next question (hidden), which earlier steps each
 *  question shows on top, and which answered question is waiting for the
 *  next one while the turn runs (its progress bubble hidden). */
export function clarificationFlowOf(messages: Message[], isLoading: boolean) {
  const hidden = new Set<number>();
  const previous = new Map<number, PreviousClarificationStep[]>();
  let waiting = -1;
  // Messages that render nothing never break a run of questions.
  const transparent = (m: Message) =>
    !!m.hiddenFromChat || isBotMessageEmpty(m)
    || (m.sender === 'user' && isPersistedPartEditInstruction(m.text));
  const idx = messages.map((_m, i) => i).filter(i => !transparent(messages[i]));
  // A short text-only bot reply ("Got it — checking the catalog…") between an
  // answered question and the next question is narration of the same run.
  const narration = (m: Message) => {
    if (m.sender !== 'bot' || m.isStreaming || (m.parts_results?.length) || (m.parts_list?.length)) return false;
    if ((m as any).ext_search_results || m.repair_instructions || clarificationOf(m)) return false;
    try {
      const o = JSON.parse((m.text || '').trim() || '{}');
      if (o?.validation || o?.parts_results || o?.lines || o?.supplier_suggestions) return false;
      const text = String(o?.text_message ?? '').trim();
      return text.length > 0 && text.length <= 300;
    } catch {
      return false;
    }
  };
  let chain: PreviousClarificationStep[] = [];
  let chainAt = -1;
  // The SAME run, tracked independently of what sits between the questions.
  // Adjacency alone is too fragile: one validation frame, one long "Got it —
  // all 10 parts…" over 300 chars, one progress bubble, and the card split
  // into a bubble per question. `plan_step` is the backend saying "question 2
  // of 7 of this plan", which is the fact we actually need, so a continuing
  // step folds whatever the turn happened to emit in between.
  let runChain: PreviousClarificationStep[] = [];
  let runAt = -1;
  let runPlanned = false;
  for (let k = 0; k < idx.length; k++) {
    const i = idx[k];
    const m = messages[i];
    const env = clarificationOf(m);
    if (env) {
      const step = planStepOf(env);
      const continuing = !!step && step.current > 1 && runAt >= 0;
      const foldAt = chainAt >= 0 ? chainAt : (continuing ? runAt : -1);
      const folded = chainAt >= 0 ? chain : (continuing ? runChain : []);
      if (foldAt >= 0) {
        hidden.add(foldAt);
        previous.set(i, [...folded]);
      }
      if (env.answers) {
        chain = [...(previous.get(i) || []), { questions: normalizeClarificationQuestions(env.questions) || [], answers: env.answers, requestLines: env.requestLines }];
        chainAt = i;
        runChain = chain;
        runAt = i;
        runPlanned = !!step && step.total > 1;
      } else {
        chain = [];
        chainAt = -1;
        // A question that does NOT continue a plan starts a new run.
        if (!continuing) { runChain = []; runAt = -1; runPlanned = false; }
      }
      continue;
    }
    // Results end the run outright — a later question is a new one and must
    // never swallow the results bubble.
    if (rendersResultsFor(m)) { runChain = []; runAt = -1; runPlanned = false; }
    // The answered card holds the screen while the next question is fetched —
    // it keeps its place and shows a spinner instead of the page reflowing
    // around a progress bubble. `runAt` is the fallback so this still works
    // when something between the steps already broke the adjacency chain.
    const anchor = chainAt >= 0 ? chainAt : runAt;
    if (anchor >= 0) {
      const running = isLoading && m.sender === 'bot' && i === messages.length - 1
        && !(m.parts_results && m.parts_results.length);
      if (running) {
        hidden.add(i);
        waiting = anchor;
        break;
      }
      const nextIsQuestion = k + 1 < idx.length && !!clarificationOf(messages[idx[k + 1]]);
      const lastAndLoading = isLoading && k === idx.length - 1;
      // Mid-plan, ANY bot bubble that is only talk folds away — not just a
      // short text_message. The turn between two steps also emits a validation
      // frame ("Got it — all 10 parts, including …"), which blows past the
      // 300-char narration limit and carries `validation`, so it landed
      // between the answered card and the next question and shoved the page
      // down. Outside a planned run the conservative rule still applies.
      const talkOnly = runPlanned && m.sender === 'bot' && !m.isStreaming
        && !rendersResultsFor(m) && !m.repair_instructions && !clarificationOf(m);
      if ((narration(m) || talkOnly) && (nextIsQuestion || lastAndLoading)) {
        hidden.add(i);
        if (lastAndLoading) waiting = anchor;
        continue;
      }
    }
    chain = [];
    chainAt = -1;
  }
  // Loading, and the answer is the last thing on screen (no bubble yet).
  if (waiting < 0 && isLoading && chainAt >= 0 && idx[idx.length - 1] === chainAt) {
    waiting = chainAt;
  } else if (waiting < 0 && isLoading && chainAt < 0 && runAt >= 0) {
    // Mid-run, with narration or a validation frame already on screen after the
    // answer: the answered card is still the thing to hold and spin.
    waiting = runAt;
  }
  return { hidden, previous, waiting };
}

function isBotMessageEmpty(message: Message): boolean {
  if (message.sender !== 'bot') return false;
  if (message.isStreaming) return false;
  if (message.repair_instructions) return false;
  if (message.parts_results && message.parts_results.length > 0) return false;
  if (message.parts_list && message.parts_list.length > 0) return false;
  if ((message as any).ext_search_results) return false;

  try {
    const parsed = JSON.parse(message.text || '{}');
    const textMessage: string = parsed?.text_message ?? message.text ?? '';
    const callToAction: string = parsed?.callToAction ?? '';

    // Has visible text or a call-to-action → not empty
    if (callToAction && callToAction.trim()) return false;

    // Data-source (vector search) suggestions render their own block even
    // with no parts_results — a suggestions-only bubble is not empty.
    if (parsed?.supplier_suggestions && Object.keys(parsed.supplier_suggestions).length > 0) return false;

    // NOTE: Do NOT suppress is_greeting=true bubbles — the automotive expert's
    // greeting uses is_greeting=true with empty assessment_text and renders via
    // hardcoded i18n text in BotMessage. Empty continuation placeholders are
    // already dropped in useStreamSend's onComplete via !hasFinalEvent && !hasText.

    if (textMessage && textMessage.trim()) return false;

    // ORDER_CREATED-only messages (action tag in text_message or raw JSON)
    const fullText = message.text ?? '';
    if (
      (textMessage.includes('ORDER_CREATED') && textMessage.includes('class="action"')) ||
      (fullText.includes('ORDER_CREATED') && fullText.includes('class=\\"action\\"'))
    ) return true;

    // Nothing to show
    return true;
  } catch {
    // Unparseable — show only if raw text is non-empty
    return !(message.text && message.text.trim());
  }
}

interface MessageListProps {
  messages: Message[];
  /** Viewer may read but not act: order owned by someone else, shared view,
   *  completed/cancelled. Locks clarification forms and follow-up pills. */
  readOnly?: boolean;
  activeOrderId: number | null;
  hasDraftOrders: boolean;
  isLoading: boolean;
  isLoadingHistory?: boolean;
  /** True when the server is still processing in the background after a client disconnect. */
  isBackgroundProcessing?: boolean;
  expandedErrors: Set<string>;
  addingToCartItems: Set<string>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  errorTooltipRef: React.RefObject<HTMLDivElement | null>;
  onRetry: (message: Message) => void;
  onFeedback: (messageId: string, helpful: boolean) => void;
  onSearchPart: (searchText: string) => void;
  /** Called to send a message programmatically without showing it in the chat input */
  onSendSilent?: (text: string) => void;
  onSendMessage?: (text: string) => void;
  /** Called when the user submits an in-bubble structured clarification form. */
  onSubmitClarification?: (message: string, structuredAnswer: StructuredAnswer) => void;
  onAddToCart: (part: any, structuredData: any) => void;
  onToggleErrorDetails: (messageId: string) => void;
  onCopyErrorDetails: (errorDetails: any, messageId: string) => void;
  formatTime: (date: Date) => string;
  isValidImageData: (imageData: string | null) => boolean;
  onOrderUpdate?: () => void;
  orderUpdateTrigger?: number;
  userPhotoURL?: string | null;
  userDisplayName?: string | null;
  cartItems?: any[];
  guestHistory?: ConversationMessage[];
  guestHistoryBuyer?: string | null;
  onSummaryItems?: (items: string[]) => void;
  /** Lifts validation parts to the page-level floating tracker panel. */
  onValidationParts?: (payload: ValidationPartsPayload) => void;
  /** Cumulative session validation parts (the tracker rows). Threaded to the
   *  result cards so each card header renders the SAME label as its tracker row. */
  validationItems?: SummaryItem[];
  /** Message ids whose deferred complementary suggestions are still expected —
   *  their "You might also need" slot renders a reserved skeleton. */
  complementaryPending?: Set<string>;
}

const MessageList: React.FC<MessageListProps> = React.memo(({
  messages,
  readOnly = false,
  activeOrderId,
  hasDraftOrders,
  isLoading,
  isLoadingHistory = false,
  isBackgroundProcessing = false,
  expandedErrors,
  addingToCartItems,
  messagesEndRef,
  errorTooltipRef,
  onRetry,
  onFeedback,
  onSearchPart,
  onSendSilent,
  onSendMessage,
  onSubmitClarification,
  onAddToCart,
  onToggleErrorDetails,
  onCopyErrorDetails,
  formatTime,
  isValidImageData,
  onOrderUpdate,
  orderUpdateTrigger,
  userPhotoURL,
  userDisplayName,
  cartItems,
  guestHistory,
  guestHistoryBuyer,
  onSummaryItems,
  onValidationParts,
  validationItems,
  complementaryPending,
}) => {
  const { t } = useTranslation();
  const [guestHistoryExpanded, setGuestHistoryExpanded] = useState(false);

  // Which bubble renders which annotated images — computed once over the whole
  // list so a marked photo appears EXACTLY ONCE. The same artifact arrives on
  // the hidden extraction_result bubble, on the turn's terminal frame, and again
  // (re-signed) on every history reload; without a single assignment pass the
  // user sees the same marked photo repeated down the thread.
  const persistedImagesMap = React.useMemo(
    () => buildAnnotatedImageAssignments(messages),
    [messages],
  );

  // Compute conversation-wide facts once. These used to scan all messages from
  // inside messages.map, making every popup-triggered render quadratic in the
  // length of the conversation.
  const sessionHasLedger = messages.some(
    m => m.sender === 'bot' && Array.isArray((m as any).lines) && (m as any).lines.length > 0,
  );
  const clarificationFlow = clarificationFlowOf(messages, isLoading);
  let latestResultsIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].sender === 'user') break;
    if (rendersResultsFor(messages[i])) {
      latestResultsIndex = i;
      break;
    }
  }

  const renderPartEditIndicator = (key: React.Key, text: string) => {
    const edits = parsePersistedPartEdits(text);
    return (
      <div key={key} className="my-4 flex justify-center" role="status">
        <div className="flex max-w-lg items-start gap-2 rounded-xl border border-violet-200/80 bg-violet-50/90 px-3 py-2 text-xs text-violet-800 shadow-sm">
          <PencilLine className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <div className="font-semibold">
              {t('chat.partEdit.updatedIndicator', { defaultValue: 'Part search updated' })}
            </div>
            {edits.length > 0 && (
              <div className="mt-0.5 space-y-0.5 text-violet-700/90">
                {edits.map((edit, index) => (
                  <div key={`${edit.original}-${edit.value}-${index}`} className="break-words">
                    {partEditKind(edit) === 'add' ? (
                      <>
                        <span className="opacity-70">{t('chat.partEdit.added', { defaultValue: 'Added' })}</span>
                        <span className="mx-1.5" aria-hidden="true">+</span>
                        <span className="font-medium">{edit.value}</span>
                      </>
                    ) : partEditKind(edit) === 'remove' ? (
                      <>
                        <span className="opacity-70">{t('chat.partEdit.removed', { defaultValue: 'Removed' })}</span>
                        <span className="mx-1.5" aria-hidden="true">−</span>
                        <span className="line-through font-medium">{edit.original}</span>
                      </>
                    ) : (
                      <>
                        <span className="line-through opacity-70">{edit.original}</span>
                        <span className="mx-1.5" aria-hidden="true">→</span>
                        <span className="font-medium">{edit.value}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  if (isLoadingHistory && messages.length === 0) {
    return (
      <div className="flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-3 sm:px-4 pt-4 sm:p-4">
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md p-8">
            <div className="relative w-16 h-16 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-gray-100"></div>
              <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
            </div>
            <p className="text-gray-600 text-sm font-light">
              {t('chat.loadingHistory', { defaultValue: 'Loading conversation history...' })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tap-pad flex-1 space-y-6 overflow-y-auto overflow-x-hidden px-2 sm:px-6 pt-6" style={{ paddingBottom: readOnly ? 20 : 'calc(var(--composer-h, 120px) + 16px)' }}>
      {/* 5xl, not 4xl, so the wide results table has room to breathe. Reading
          measure is unaffected: text bubbles cap themselves far narrower
          (bot xl:w-[28rem], user xl:max-w-lg), so only the table-mode bubble —
          which caps at max-w-full — actually uses the extra width. */}
      <div className="max-w-5xl mx-auto">
        <CatalogStatusStrip messages={messages} />

        {/* Guest / Client History Section */}
        {guestHistory && guestHistory.length > 0 && (() => {
          // Pre-filter to only renderable messages
          const renderableGuestMsgs = guestHistory
            .map((msg: any, idx: number) => ({ msg, idx, message: guestMsgToMessage(msg) }))
            .filter(({ message }) => message !== null) as { msg: any; idx: number; message: Message }[];

          const totalRenderable = renderableGuestMsgs.length;
          const hasCollapsible = totalRenderable > 2;

          // When collapsed: show only first and last renderable message
          const visibleItems = !hasCollapsible || guestHistoryExpanded
            ? renderableGuestMsgs
            : [renderableGuestMsgs[0], renderableGuestMsgs[totalRenderable - 1]];

          const hiddenCount = totalRenderable - 2;

          const renderGuestMessage = ({ message, idx }: { message: Message; idx: number }) => (
            <div key={message.id ?? idx} className="w-full msg-enter">
              {message.sender === 'user' && isPersistedPartEditInstruction(message.text) ? (
                renderPartEditIndicator(`guest-edit-${message.id ?? idx}`, message.text)
              ) : message.sender === 'user' ? (
                <UserMessage
                  message={message}
                  isLoading={false}
                  expandedErrors={expandedErrors}
                  errorTooltipRef={errorTooltipRef}
                  onRetry={onRetry}
                  onToggleErrorDetails={onToggleErrorDetails}
                  onCopyErrorDetails={onCopyErrorDetails}
                  formatTime={formatTime}
                  userPhotoURL={userPhotoURL}
                  userDisplayName={userDisplayName}
                />
              ) : (
                <BotMessage
                  message={message}
                  isLastMessage={false}
                  isLoading={false}
                  addingToCartItems={addingToCartItems}
                  onRetry={onRetry}
                  onFeedback={onFeedback}
                  onSearchPart={onSearchPart}
                  onAddToCart={onAddToCart}
                  formatTime={formatTime}
                  isValidImageData={isValidImageData}
                  onOrderUpdate={onOrderUpdate}
                  orderUpdateTrigger={orderUpdateTrigger}
                  cartItems={cartItems}
                  // Guest history is read-only: forms, follow-ups and cart are
                  // locked, but images open and text can be copied. Do NOT pass
                  // onSummaryItems here so old history messages don't
                  // populate/duplicate the bar.
                  readOnly
                />
              )}
            </div>
          );

          return (
            <div className="mb-6 relative">
              <div>
                {/* Section header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <History className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-amber-800">
                      {t('chat.guestHistory.title', { defaultValue: 'Client History' })}
                    </span>
                    {guestHistoryBuyer && (
                      <span className="text-xs text-amber-600 font-medium">
                        — {guestHistoryBuyer}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 h-px bg-amber-200" />
                </div>

                {/* Messages */}
                <div className="space-y-6">
                  {/* First message (always shown) */}
                  {hasCollapsible && !guestHistoryExpanded
                    ? renderGuestMessage(visibleItems[0])
                    : null}

                  {/* All messages when expanded, or all when no collapsing needed */}
                  {(!hasCollapsible || guestHistoryExpanded) &&
                    visibleItems.map(renderGuestMessage)}
                </div>
              </div>

              {/* Collapse toggle */}
              {hasCollapsible && (
                <div className="relative z-20 my-3 flex flex-col items-center gap-1">
                  <button
                    onClick={() => setGuestHistoryExpanded(prev => !prev)}
                    className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium hover:bg-amber-100 transition-colors cursor-pointer"
                  >
                    {guestHistoryExpanded ? (
                      <>
                        <ChevronUp className="w-3.5 h-3.5" />
                        {t('chat.guestHistory.collapse', { defaultValue: 'Collapse history' })}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3.5 h-3.5" />
                        {t('chat.guestHistory.showMore', {
                          defaultValue: `Show ${hiddenCount} more messages`,
                          count: hiddenCount,
                        })}
                      </>
                    )}
                  </button>
                  {/* Dotted line connecting first → toggle → last when collapsed */}
                  {!guestHistoryExpanded && (
                    <div className="w-px h-4 border-l-2 border-dashed border-amber-300" />
                  )}
                </div>
              )}

              {/* Last message (always shown when collapsed and collapsible) */}
              {hasCollapsible && !guestHistoryExpanded && (
                <div>
                  <div className="space-y-6">
                    {renderGuestMessage(visibleItems[1])}
                  </div>
                </div>
              )}

              {/* Divider between guest history and current chat */}
              <div className="relative mt-6 mb-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-dashed border-gray-300" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-[#F6F4F0] px-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t('chat.guestHistory.currentChatDivider', { defaultValue: 'Current Chat' })}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {messages.map((message, index) => {
          // One card for a whole narrowing: an answered question followed by
          // the next question is folded into it (its answers shown on top),
          // and while the next one is being prepared the answered card shows
          // "Narrowing it down…" instead of a separate progress bubble.
          if (clarificationFlow.hidden.has(index)) return null;
          if (message.sender === 'user' && isPersistedPartEditInstruction(message.text)) {
            return renderPartEditIndicator(`part-edit-${message.id}`, message.text);
          }
          if (message.hiddenFromChat) return null;
          if (isBotMessageEmpty(message)) return null;
          const isLastMessage = index === messages.length - 1;
          const rendersResults = rendersResultsFor(message);

          // Is this the most recent bot message that produced parts_results? Only that
          // table incorporates the live cumulative validation list; older results tables
          // stay frozen to their own snapshot so a later search updates only the tracker.
          //
          // A NEWER USER MESSAGE also freezes this table: the moment the user starts a
          // new turn, its search is in-flight and has produced no parts_results yet — so
          // without this guard THIS message would stay "latest" and absorb the in-flight
          // parts from the live cumulative validation list, leaking them into the
          // previous table as spurious "not found" rows. Once the new search renders its
          // own results table, THAT one becomes latest and shows the merged history.
          //
          // "Results" means anything the results view renders — cards, a settled
          // ledger turn, or plain misses. A turn that confirmed nothing is still
          // the latest results, and its unresolved rows must stay editable.
          const isLatestResults = index === latestResultsIndex;

          // Build a map of OE part numbers (lowercased) → anchor element id shown in all
          // previous bot messages so MultiPartResultView can show a "brought before" notice
          // with a scroll link instead of silently suppressing duplicates.
          let seenPartNumbers: Map<string, string> | undefined;
          // Every part found in EARLIER bot messages, so this iteration's results
          // table can offer a collapsed "full list" (history) view (grayed out).
          // Once any message carries a real ledger, messages without one are
          // narration, not a second copy of a table (see MultiPartResultView).
          let priorParts: any[] | undefined;
          // Every bot message that can render results needs the earlier cards —
          // a turn that confirmed nothing still offers the rest of the order,
          // and those rows show their parts only if the cards are at hand.
          if (rendersResults) {
            seenPartNumbers = new Map<string, string>();
            priorParts = [];
            for (let i = 0; i < index; i++) {
              const prev = messages[i];
              if (prev.sender !== 'bot' || !prev.parts_results) continue;
              priorParts.push(...prev.parts_results);
              prev.parts_results.forEach((pr: any, prIdx: number) => {
                // Try hero part number
                let pn: string | undefined;
                if (pr.image_parts && Array.isArray(pr.image_parts) && pr.part_callout_id) {
                  const hero = (pr.image_parts as any[]).find((p: any) =>
                    p.crossCatKey == pr.part_callout_id ||
                    String(p.index) === pr.part_callout_id ||
                    p.calloutLabel === pr.part_callout_id ||
                    String(p.callout) === pr.part_callout_id ||
                    p.part_callout_id === pr.part_callout_id
                  );
                  pn = hero?.partNumber || hero?.part_number || hero?.crossCatKey;
                }
                if (!pn) pn = pr.structured_data?.part_data?.part_number || pr.structured_data?.part_data?.part_id;
                if (pn) seenPartNumbers!.set(pn.toLowerCase(), `${prev.id}-part-${prIdx}`);
              });
            }
          }

          return (
          // Keyed by POSITION, not message.id: the deferred complementary round
          // re-loads history ~2.5s after a turn closes and the server ids
          // (event.id) replace the optimistic stream ids (`bot_<ts>`). Keying on
          // the id made every message unmount and remount on that swap — the
          // whole conversation replayed its entrance animation, results views
          // lost their local state (view mode, zoom) and the reflow yanked the
          // page under the reader's cursor. The list is append-only, so the
          // index is the stable identity; the list itself is remounted per
          // session (see ChatPage) so nothing is reused across conversations.
          <div key={index} className="w-full mb-6 msg-enter">
            {message.sender === 'user' ? (
              <UserMessage
                message={message}
                isLoading={isLoading}
                isLastMessage={isLastMessage}
                expandedErrors={expandedErrors}
                errorTooltipRef={errorTooltipRef}
                onRetry={onRetry}
                onToggleErrorDetails={onToggleErrorDetails}
                onCopyErrorDetails={onCopyErrorDetails}
                formatTime={formatTime}
                userPhotoURL={userPhotoURL}
                userDisplayName={userDisplayName}
              />
            ) : (
              <BotMessage
                message={message}
                isLastMessage={isLastMessage}
                isLoading={isLoading}
                addingToCartItems={addingToCartItems}
                onRetry={onRetry}
                onFeedback={onFeedback}
                onSearchPart={onSearchPart}
                onSendSilent={onSendSilent}
                onSendMessage={onSendMessage}
                previousClarifications={clarificationFlow.previous.get(index)}
                waitingNextQuestion={clarificationFlow.waiting === index}
                onSubmitClarification={onSubmitClarification}
                readOnly={readOnly}
                onAddToCart={onAddToCart}
                formatTime={formatTime}
                isValidImageData={isValidImageData}
                onOrderUpdate={onOrderUpdate}
                orderUpdateTrigger={orderUpdateTrigger}
                cartItems={cartItems}
                onSummaryItems={onSummaryItems}
                onValidationParts={onValidationParts}
                validationItems={validationItems}
                complementaryPending={!!complementaryPending?.has(String(message.id))}
                isLatestResults={isLatestResults}
                persistedAnnotatedImages={persistedImagesMap.get(message.id)}
                seenPartNumbers={seenPartNumbers}
                priorParts={priorParts}
                sessionHasLedger={sessionHasLedger}
              />
                
            )}
          </div>
        )})}

        {/* Background-processing indicator: server is still running the action loop
            after the client disconnected. Matches the exact layout of a streaming BotMessage. */}
        {isBackgroundProcessing && (
          <div className="w-full mb-6 msg-enter">
            <div className="flex gap-2 sm:gap-3 flex-col max-w-full">
              <div className="flex gap-2 sm:gap-3 flex-row">
                {/* Same lottie avatar as BotMessage when isStreaming */}
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full hidden sm:flex items-center justify-center flex-shrink-0 bg-transparent">
                  <DotLottieReact
                    src={getVersionedPublicUrl('/cata_loader.lottie')}
                    loop
                    autoplay
                    style={{ width: 32, height: 32 }}
                  />
                </div>
                {/* Same white bubble as BotMessage */}
                <div className="w-full sm:w-80 lg:w-96 xl:w-[28rem] p-2 sm:p-3 rounded-2xl shadow-sm break-words overflow-hidden bg-white text-gray-900">
                  <StreamingIndicator
                    message={{
                      id: '__background__',
                      text: '',
                      sender: 'bot',
                      timestamp: new Date(),
                      vin: null,
                      datasetId: null,
                      isStreaming: true,
                      events: [],
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
});

export default MessageList;
